import { useEffect, useState, useRef } from "react";
import CRMSidebar from "./sidebar";
import UIkit from "uikit";
import CRMService from "../../services/crm.service";
import Pagination from "../common/Pagination";
import { ActivityFormOffcanvas } from "./activity-panel";

const todayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function OpenTasks() {
  const readOnlyPreview = CRMService.isTaskListReadOnlyPreview();
  const [filters, setFilters] = useState({ search: "", assignee: "all", due: "all", page: 0 });
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [revision, setRevision] = useState(0);
  const saving = useRef(false);
  const change = (key, value) => setFilters((old) => ({ ...old, [key]: value, page: 0 }));
  useEffect(() => {
    const timer = setTimeout(() => setFilters((old) => old.search === search ? old : { ...old, search, page: 0 }), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    CRMService.getTaskAssignees().then(({ data }) => { if (active) setAssignees(data); })
      .catch(() => { if (active) setError("Unable to load assignee choices. Reload the page to retry."); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    CRMService.listOpenTasks({ ...filters, per: 25, today: todayString() }).then(({ data }) => {
      if (!active) return;
      if (filters.page > 0 && !data.items.length && data.total <= filters.page * 25) {
        setFilters((old) => ({ ...old, page: Math.max(0, Math.ceil(data.total / 25) - 1) }));
      } else { setData(data); }
    }).catch(() => { if (active) setError("Unable to load tasks. Reload the page to retry."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, revision]);
  useEffect(() => {
    if (editing) UIkit.offcanvas("#offcanvas-crm-activity-form").show();
  }, [editing]);
  const save = async (type, payload) => {
    if (readOnlyPreview) return;
    if (saving.current) return;
    saving.current = true;
    try {
      await CRMService.updateTask(payload.id, payload);
      UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
      setEditing(null); setRevision((n) => n + 1);
      UIkit.notification("Task updated", "success");
    } catch (_) { UIkit.notification("Unable to save task. Please try again.", "danger"); }
    finally { saving.current = false; }
  };
  const group = (label, items) => items.length > 0 && <section className="crm-task-group">
    <h3>{label}</h3>
    <div className="uk-overflow-auto"><table className="uk-table uk-table-hover uk-table-divider hms-table">
      <thead><tr><th>Task</th><th>Status</th><th>Related to</th><th>Assigned to</th><th>Due date</th></tr></thead>
      <tbody>{items.map((task) => <tr key={task.id}>
        <td>{readOnlyPreview ? (task.title || "Untitled task") : <button className="uk-button uk-button-link crm-task-title" type="button" onClick={() => setEditing({ ...task, status: ["in-progress", "in_progress"].includes(task.status) ? "started" : task.status })}>{task.title || "Untitled task"}</button>}</td>
        <td><div className="hms-booking-tag">{["new", "open"].includes(task.status) ? "Open" : "In progress"}</div></td>
        <td>{[task.guest_name, task.property_name, ...(task.work_order_assignees || [])
          .filter((a) => a.assignment_role === "owner").map((a) => a.assignee_name)].filter(Boolean).join(" · ") || "—"}</td>
        <td>{(task.work_order_assignees || []).filter((a) => a.assignment_role !== "owner")
          .map((a) => a.assignee_name).join(", ") || "Unassigned"}</td>
        <td>{task.due_at ? task.due_at.split("-").slice(1).concat(task.due_at.slice(0, 4)).join("/") : "No date"}</td>
      </tr>)}</tbody>
    </table></div>
  </section>;
  return <div className="crm-layout">
    <CRMSidebar activeKey="open-tasks" />
    <main className="crm-open-tasks-main">
      <h2>Open Tasks</h2>
      {readOnlyPreview && <p className="uk-text-meta">Staging preview · Read-only</p>}
      <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
      <div className="crm-task-filters uk-form-stacked uk-margin-bottom">
          <div className="crm-task-search">
            <div className="uk-search uk-search-default">
              {search ? <button type="button" className="uk-form-icon-flip uk-form-icon"
                aria-label="Clear task search" uk-icon="icon: close" onClick={() => setSearch("")} />
                : <span className="uk-search-icon-flip" uk-search-icon="true" />}
              <input id="crm-task-search" className="uk-search-input" type="search" aria-label="Search tasks"
                placeholder="Search Tasks" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="crm-task-filter">
            <select id="crm-task-assignee" className="uk-select" aria-label="Assigned to" value={filters.assignee}
              onChange={(e) => change("assignee", e.target.value)}>
              <option value="all">All tasks</option><option value="mine">My tasks</option><option value="unassigned">Unassigned</option>
              {assignees.map((a) => <option value={a.id} key={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="crm-task-filter">
            <select id="crm-task-due" className="uk-select" aria-label="Due date" value={filters.due}
              onChange={(e) => change("due", e.target.value)}>
              <option value="all">All dates</option><option value="overdue">Overdue</option><option value="today">Today</option>
              <option value="upcoming">Upcoming</option><option value="none">No date</option>
            </select>
          </div>
      </div>
      {(filters.assignee !== "all" || filters.due !== "all") && <div className="hms-active-filters uk-margin-small-bottom">
        <span className="hms-active-filters-label">Filters:</span>
        {filters.assignee !== "all" && <span className="hms-filter-chip">
          Assigned to: {filters.assignee === "mine" ? "My tasks" : filters.assignee === "unassigned" ? "Unassigned" : assignees.find((a) => String(a.id) === String(filters.assignee))?.name || filters.assignee}
          <button type="button" className="hms-filter-chip-close" aria-label="Remove assignee filter" onClick={() => change("assignee", "all")}>×</button>
        </span>}
        {filters.due !== "all" && <span className="hms-filter-chip">
          Due date: {{ overdue: "Overdue", today: "Today", upcoming: "Upcoming", none: "No date" }[filters.due]}
          <button type="button" className="hms-filter-chip-close" aria-label="Remove due date filter" onClick={() => change("due", "all")}>×</button>
        </span>}
        <button type="button" className="hms-filter-chip-clear-all" onClick={() => setFilters((old) => ({ ...old, assignee: "all", due: "all", page: 0 }))}>Clear all</button>
      </div>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p role="status">Loading tasks…</p> : !error && <>
        <p>{data.total.toLocaleString()} open {data.total === 1 ? "task" : "tasks"}</p>
        {!data.items.length && <div className="uk-overflow-auto"><table className="uk-table uk-table-hover uk-table-divider hms-table">
          <thead><tr><th>Task</th><th>Status</th><th>Related to</th><th>Assigned to</th><th>Due date</th></tr></thead>
          <tbody><tr><td colSpan={5} style={{ padding: "30px 25px" }}>No open tasks match these filters.</td></tr></tbody>
        </table></div>}
        {group("No date", data.items.filter((t) => !t.due_at))}
        {group("Scheduled", data.items.filter((t) => t.due_at))}
      </>}
      </div>
      <Pagination index={filters.page} count={error ? 0 : data.total} perPage={25}
        onIndexChanged={(page) => setFilters((old) => old.page === page ? old : { ...old, page })} />
      {!readOnlyPreview && <ActivityFormOffcanvas formType={editing ? "task" : null} editingItem={editing}
        taskOwnerValue={(editing?.work_order_assignees || []).filter((a) => a.assignment_role === "owner")
          .map((a) => `${a.assignee_type}_${a.assignee_id}`)[0] || null}
        onSave={save} onClose={() => setEditing(null)} />}
    </main>
  </div>;
}
